import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { X, Bell, Check, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { EmptyState } from "@/components/common";

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export function NotificationPanel({ open, onClose, onChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications");
      setItems(data.notifications || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const readAll = async () => {
    await api.post("/notifications/read-all");
    setItems((x) => x.map((n) => ({ ...n, read: true })));
    onChange?.();
  };

  const open1 = async (n) => {
    if (!n.read) {
      await api.post(`/notifications/${n.id}/read`);
      onChange?.();
    }
    onClose();
    if (n.link) nav(n.link);
  };

  const remove = async (e, n) => {
    e.stopPropagation();
    await api.delete(`/notifications/${n.id}`);
    setItems((x) => x.filter((i) => i.id !== n.id));
    onChange?.();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70]">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="absolute right-0 top-0 bottom-0 w-full sm:w-96 iks-glass border-l border-white/10 flex flex-col iks-pop">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 safe-top">
          <h3 className="font-display font-semibold text-slate-100 flex items-center gap-2">
            <Bell size={18} className="text-sky-400" /> Notifications
          </h3>
          <div className="flex items-center gap-1">
            <button
              onClick={readAll}
              data-testid="notif-read-all"
              className="text-xs text-sky-400 hover:text-sky-300 px-2 py-1 rounded-lg hover:bg-white/5"
            >
              Mark all read
            </button>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-100 p-1">
              <X size={20} />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-3">
          {loading ? (
            <p className="text-center text-slate-500 text-sm py-10">Loading…</p>
          ) : items.length === 0 ? (
            <div className="mt-6">
              <EmptyState icon={Bell} title="You're all caught up" subtitle="New activity from your connections will show up here." />
            </div>
          ) : (
            items.map((n) => (
              <button
                key={n.id}
                onClick={() => open1(n)}
                data-testid="notif-item"
                className={`w-full text-left rounded-xl p-3.5 mb-2 border transition-all group ${
                  n.read ? "bg-transparent border-white/5" : "bg-blue-600/10 border-sky-500/20"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-100">{n.title}</p>
                    {n.body && <p className="text-xs text-slate-400 mt-0.5">{n.body}</p>}
                    <p className="text-[11px] text-slate-600 mt-1">{timeAgo(n.created_at)}</p>
                  </div>
                  <span
                    onClick={(e) => remove(e, n)}
                    className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-red-400 p-1 transition-all"
                  >
                    <Trash2 size={15} />
                  </span>
                </div>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
